import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  ButtonBase,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ChevronDown, MoreVertical, Plus, X } from 'lucide-react';
import { SkillMatchEntry } from '../../../../utils/types/candi_bank_types';

interface SkillChipRowProps {
  label: string;
  emptyHint?: string;
  badgeBg: string;
  badgeText: string;
  skills: string[];
  skillMatches: SkillMatchEntry[];
  coverage: Record<string, number>;
  tiers: { label: string; key: string }[];
  onSkillsChange: (skills: string[]) => void;
  onMoveTo: (skill: string, targetTier: string) => void;
  onRemoveMatch: (skill: string, match: string) => void;
  isReindexing: boolean;
}

export const SkillChipRow: React.FC<SkillChipRowProps> = ({
  label,
  emptyHint,
  badgeBg,
  badgeText,
  skills,
  skillMatches,
  coverage,
  tiers,
  onSkillsChange,
  onMoveTo,
  onRemoveMatch,
  isReindexing,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [expandedSkills, setExpandedSkills] = useState<Record<string, boolean>>({});
  const [menuAnchor, setMenuAnchor] = useState<{
    element: HTMLElement;
    skill: string;
  } | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isAdding) {
      inputRef.current?.focus();
    }
  }, [isAdding]);

  const handleAddSkill = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      const newSkill = inputValue.trim();
      if (!skills.includes(newSkill)) {
        onSkillsChange([...skills, newSkill]);
      }
      setInputValue('');
      setIsAdding(false);
    } else if (e.key === 'Escape') {
      setIsAdding(false);
      setInputValue('');
    }
  };

  const removeSkill = (skillToRemove: string) => {
    onSkillsChange(skills.filter((s) => s !== skillToRemove));
    if (expandedSkills[skillToRemove]) {
      setExpandedSkills((prev) => ({ ...prev, [skillToRemove]: false }));
    }
  };

  const toggleExpand = (skill: string) => {
    setExpandedSkills((prev) => ({ ...prev, [skill]: !prev[skill] }));
  };

  const isCoverageLoaded = Object.keys(coverage).length > 0;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { sm: 'flex-start' },
        gap: 1.5,
        py: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      {/* Tier Label */}
      <Box
        sx={{
          width: { xs: 'auto', sm: 110 },
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          pt: 0.5,
        }}
      >
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            px: 1,
            py: 0.25,
            borderRadius: 1.5,
            fontSize: '11px',
            fontWeight: 700,
            bgcolor: badgeBg,
            color: badgeText,
          }}
        >
          {label}
        </Box>
        {isReindexing && (
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              animation: 'ping 1s cubic-bezier(0, 0, 0.2, 1) infinite',
            }}
            title='Re-indexing'
          />
        )}
      </Box>

      {/* Chips Container */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1,
          minWidth: 0,
        }}
      >
        {skills.length === 0 && !isAdding && (
          <Typography variant='caption' color='text.disabled' fontStyle='italic'>
            {emptyHint || 'None'}
          </Typography>
        )}

        {skills.map((skill) => {
          const isZeroCoverage = isCoverageLoaded && coverage[skill] === 0;
          const isExpanded = expandedSkills[skill];
          const matchesForSkill =
            skillMatches.find((m) => m.skill === skill)?.matches || [];
          const hasMatches = matchesForSkill.length > 0;

          return (
            <Box key={skill} sx={{ display: 'inline-flex', flexDirection: 'column' }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: isZeroCoverage ? '#fcd34d' : 'divider',
                  bgcolor: isZeroCoverage ? '#fffbeb' : '#ffffff',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                }}
              >
                <ButtonBase
                  onClick={() => toggleExpand(skill)}
                  sx={{
                    px: 1,
                    py: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    borderRadius: '8px 0 0 8px',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                  title={isZeroCoverage ? 'Nobody in your pool has this' : undefined}
                >
                  {isZeroCoverage && (
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: '#f59e0b',
                      }}
                    />
                  )}
                  <Typography variant='caption' fontWeight={600} color='text.primary'>
                    {skill}
                  </Typography>
                  {hasMatches && (
                    <ChevronDown
                      size={12}
                      style={{
                        transform: isExpanded ? 'rotate(180deg)' : 'none',
                        transition: 'transform 0.2s',
                        color: '#94a3b8',
                      }}
                    />
                  )}
                </ButtonBase>

                <IconButton
                  size='small'
                  onClick={() => removeSkill(skill)}
                  sx={{
                    p: 0.5,
                    borderLeft: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 0,
                    color: 'text.disabled',
                    '&:hover': { color: 'error.main', bgcolor: '#fee2e2' },
                  }}
                  title='Remove skill'
                >
                  <X size={12} />
                </IconButton>

                <IconButton
                  size='small'
                  onClick={(e) => setMenuAnchor({ element: e.currentTarget, skill })}
                  sx={{
                    p: 0.5,
                    borderLeft: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '0 8px 8px 0',
                    color: 'text.disabled',
                    '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
                  }}
                  title='Move tier'
                >
                  <MoreVertical size={12} />
                </IconButton>
              </Box>

              {/* Phrasings expansion */}
              {isExpanded && hasMatches && (
                <Stack
                  direction='row'
                  spacing={0.5}
                  flexWrap='wrap'
                  sx={{
                    mt: 0.75,
                    ml: 1,
                    pl: 1,
                    borderLeft: '2px solid',
                    borderColor: 'primary.light',
                  }}
                >
                  {matchesForSkill.map((match) => (
                    <Chip
                      key={match}
                      size='small'
                      label={match}
                      onDelete={() => onRemoveMatch(skill, match)}
                      deleteIcon={<X size={10} />}
                      sx={{
                        height: 20,
                        fontSize: '0.6875rem',
                        bgcolor: 'grey.100',
                      }}
                    />
                  ))}
                </Stack>
              )}
            </Box>
          );
        })}

        {/* Add skill input/button */}
        {isAdding ? (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              bgcolor: '#ffffff',
              border: '1px solid',
              borderColor: 'primary.main',
              borderRadius: 2,
              px: 1,
              py: 0.25,
            }}
          >
            <input
              ref={inputRef}
              type='text'
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleAddSkill}
              onBlur={() => {
                if (!inputValue.trim()) setIsAdding(false);
              }}
              placeholder='Skill name...'
              style={{
                outline: 'none',
                border: 'none',
                fontSize: '0.75rem',
                color: '#1e293b',
                width: 80,
                background: 'transparent',
              }}
            />
          </Box>
        ) : (
          <ButtonBase
            onClick={() => setIsAdding(true)}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1,
              py: 0.5,
              borderRadius: 2,
              border: '1px dashed',
              borderColor: 'divider',
              fontSize: '0.6875rem',
              fontWeight: 700,
              color: 'text.secondary',
              bgcolor: 'rgba(248, 250, 252, 0.7)',
              '&:hover': {
                borderColor: 'primary.main',
                color: 'primary.main',
                bgcolor: '#ffffff',
              },
            }}
          >
            <Plus size={12} /> Add
          </ButtonBase>
        )}
      </Box>

      {/* Move to Tier Menu */}
      <Menu
        anchorEl={menuAnchor?.element}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
            minWidth: 140,
          },
        }}
      >
        <Typography
          variant='caption'
          fontWeight={700}
          color='text.disabled'
          textTransform='uppercase'
          letterSpacing='0.05em'
          sx={{ px: 2, py: 0.5, display: 'block' }}
        >
          Move to tier
        </Typography>
        {tiers.map((t) => (
          <MenuItem
            key={t.key}
            onClick={() => {
              if (menuAnchor) {
                onMoveTo(menuAnchor.skill, t.key);
                setMenuAnchor(null);
              }
            }}
            sx={{ fontSize: '0.75rem', fontWeight: 600 }}
          >
            {t.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};
